import type { LeavePeriod, PlusTimePeriod } from '@trinnvis/dabih-api-client';
import type { EmployeeForStaffingCalendar } from './employee-for-staffing-calendar';
import { LocalDate } from '../../../utilities/local-date';
import { timeInMinutes } from './time-in-minutes';
import { getScheduleDayData } from './get-schedule-day-data';
import { validPlusTimeMinutes } from './valid-plus-time-minutes';

import { periodTimeDisplayText } from './period-time-display-text';
import type { TimekeepingPeriod } from './timekeeping-period-type';

/**
 * Returns a timekeeping period with data needed for display as a list entry
 */
export function timekeepingPeriod(
  type: string,
  period: LeavePeriod | PlusTimePeriod,
  employee: EmployeeForStaffingCalendar,
  holidays: { [p: string]: string },
): TimekeepingPeriod {
  const startDate = LocalDate.fromString(period.start);
  const endDate = LocalDate.fromString(period.end);
  let startTime = '';
  let registeredStartTimeInMinutes = 0;
  if (period.startTime) {
    startTime = period.startTime;
    registeredStartTimeInMinutes = timeInMinutes(period.startTime);
  }
  let endTime = '';
  let registeredEndTimeInMinutes = 1440;
  if (period.endTime) {
    endTime = period.endTime;
    registeredEndTimeInMinutes = timeInMinutes(period.endTime);
  }
  let unconfirmedText = '';
  let prefix = '';
  let minutes = 0;
  LocalDate.dates(startDate, endDate.plusDays(1)).forEach(function (day) {
    let dayMinutes = 0;
    const scheduleDayData = getScheduleDayData(day, employee);
    const isEffectiveHoliday = scheduleDayData.holidays && holidays[day.toString()];
    let periodDayStartMinutes = registeredStartTimeInMinutes;
    if (day.isAfter(startDate)) {
      periodDayStartMinutes = 0;
    }
    let periodDayEndMinutes = registeredEndTimeInMinutes;
    if (day.isBefore(endDate)) {
      periodDayEndMinutes = 1440;
    }
    const workStartMinutes = timeInMinutes(scheduleDayData.start);
    const workEndMinutes = timeInMinutes(scheduleDayData.end);
    if (type === 'timeOff' && scheduleDayData.workHours > 0 && !isEffectiveHoliday) {
      let leaveStartMinutes = periodDayStartMinutes;
      let leaveEndMinutes = periodDayEndMinutes;
      if (periodDayStartMinutes <= workStartMinutes) {
        leaveStartMinutes = workStartMinutes;
      }
      if (workEndMinutes <= periodDayEndMinutes) {
        leaveEndMinutes = workEndMinutes;
      }
      dayMinutes = leaveEndMinutes - leaveStartMinutes;
      if (dayMinutes > scheduleDayData.workHours * 60) {
        dayMinutes = scheduleDayData.workHours * 60;
      }
    }
    if (type === 'plusTime') {
      if (scheduleDayData.workHours > 0 && !isEffectiveHoliday) {
        minutes = validPlusTimeMinutes(
          periodDayStartMinutes,
          periodDayEndMinutes,
          workStartMinutes,
          workEndMinutes,
          employee.leavePeriods,
          day,
        );
      } else {
        dayMinutes = periodDayEndMinutes - periodDayStartMinutes;
      }
    }
    minutes += dayMinutes;
  });
  if (minutes > 0) {
    if (type === 'timeOff') {
      prefix = '- ';
    }
    if (type === 'plusTime') {
      prefix = '+ ';
    }
  }
  if (!period.confirmed) {
    unconfirmedText = ' (ubekreftet)';
  }
  return {
    type: type,
    confirmed: period.confirmed,
    start: period.start,
    startTime: startTime,
    end: period.end,
    endTime: endTime,
    timeDisplay: periodTimeDisplayText(startDate, startTime, endDate, endTime, true) + unconfirmedText,
    minutes: minutes,
    hoursDisplay: prefix + (minutes / 60).toFixed(2).replace('.', ',') + ' t',
    notes: period.notes,
    id: period.uuid,
  };
}
